import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import {assert} from 'Asserts';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {SchemaTag} from 'folder-items/FileConstants';
import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import useSelectorsById, {
  SchemaFieldSelectorsDict,
  selectorsAreLoaded,
} from 'notebook/structured-tables/hooks/useSelectorsById';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {MaximumTableHeight} from 'notebook/tables/constants/TableLayoutConstants';
import {ProcessTaskStatus} from 'processes/__generated/ProcessTaskStatus';
import {EditExistingTask} from 'processes/dao/ProcessManagementDAO';
import {
  TaskUpdateConfirmationModalQuery,
  TaskUpdateConfirmationModalQueryVariables,
} from 'processes/modals/__generated/TaskUpdateConfirmationModalQuery';
import {DefaultStatusDisplayName, ProcessTableLocation} from 'processes/ProcessConstants';
import {PROCESS_TASK_STATUS_FRAGMENT} from 'processes/ProcessFragments';
import {getStatusFromId} from 'processes/status/TaskStatusUtil';
import createTaskTable, {
  CreateTaskTableSchemaWithStatusLifecycleParams,
  CreateTaskTableTaskParams,
} from 'processes/tables/createTaskTable';
import {InlineProcessTaskStatusDisplay} from 'processes/tables/ProcessTaskStatusDisplay';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';

type ProcessTask = ExtractByTypename<
  TaskUpdateConfirmationModalQuery['processTasks'][number],
  'ProcessTaskNode'
>;

export const TASK_UPDATE_CONFIRMATION_MODAL_QUERY = gql`
  query TaskUpdateConfirmationModalQuery($processTaskIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTasks(ids: $processTaskIds) {
      ... on ProcessTaskNode {
        id
        humanId
        status {
          ...ProcessTaskStatus
        }
        fields
        ...StudyableTypedValueDataGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('StudyableTypedValueData').fragment}
  ${PROCESS_TASK_STATUS_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface TaskUpdateConfirmationModalProps {
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  tasksWithTerminalStatus: Array<EditExistingTask & {statusId: string}>;
  tasksAlreadyMappedWithUpdatedFields: Array<EditExistingTask>;
  taskSchema: CreateTaskTableSchemaWithStatusLifecycleParams;
}

interface TaskUpdateConfirmationModalContentProps {
  updatedTasks: Array<EditExistingTask>;
  fetchedTasksById: {[id: string]: ProcessTask};
  taskSchema: CreateTaskTableSchemaWithStatusLifecycleParams;
  taskIdToNewStatusId?: {[id: string]: string};
  selectorsById: SchemaFieldSelectorsDict;
}

function getTaskForTable(
  updatedTask: EditExistingTask,
  fetchedTasksById: {[id: string]: ProcessTask},
  taskSchema: CreateTaskTableSchemaWithStatusLifecycleParams,
  taskIdToNewStatusId?: {[id: string]: string}
): CreateTaskTableTaskParams {
  const fetchedTask = fetchedTasksById[updatedTask.id];
  let status: ProcessTaskStatus;
  if (taskIdToNewStatusId != null) {
    const terminalStatusId = taskIdToNewStatusId[updatedTask.id];
    assert(terminalStatusId != null, 'Expected task status to be updated');
    status =
      terminalStatusId != null
        ? getStatusFromId(taskIdToNewStatusId[updatedTask.id], taskSchema.statusLifecycle)!
        : fetchedTask.status;
  } else {
    status = fetchedTask.status;
  }

  return {
    ...updatedTask,
    humanId: fetchedTask.humanId,
    fields: fetchedTask.fields.map((field: SchemaTag) => {
      const updatedFieldTag = updatedTask.fields?.[field.name];
      return updatedFieldTag != null ? {...field, ...updatedFieldTag} : field;
    }),
    studies: fetchedTask.studies,
    status,
    // The following properties are not exposed in the table and so can be safely ignored.
    archiveRecord: null,
    assignee: null,
    scheduledOnDate: null,
    executionOrigin: null,
  };
}

function ConfirmationModalContent(props: TaskUpdateConfirmationModalContentProps): JSX.Element {
  const readOnlyTasks: Array<CreateTaskTableTaskParams> = props.updatedTasks.map((task) =>
    getTaskForTable(task, props.fetchedTasksById, props.taskSchema, props.taskIdToNewStatusId)
  );

  const readOnlyTaskTable = createTaskTable({
    process: {
      // We don't need the process ID or name since this table is read-only
      id: '',
      name: '',
      processTaskSchema: {
        ...props.taskSchema,
      },
      tasks: readOnlyTasks,
    },
    tableLocation: ProcessTableLocation.STATUS_CONFIRMATION_MODAL,
    selectorsById: props.selectorsById,
    locked: true,
  });
  return (
    <div className={css(styles.taskTable)}>
      <StructuredTableOutsideNotebook
        structuredTable={readOnlyTaskTable}
        cid={readOnlyTaskTable.cid}
        updateHandler={noop}
        locked={true}
        hasPrimarySamples={false}
        hideLeftHeaderControls={true}
        enableFormulas={true}
        maximumHeight={MaximumTableHeight.AUTO_FIXED}
      />
    </div>
  );
}

function TaskUpdateConfirmationModalWithTable(props: TaskUpdateConfirmationModalProps): JSX.Element {
  const processTaskIds: Array<string> = [
    ...props.tasksWithTerminalStatus,
    ...props.tasksAlreadyMappedWithUpdatedFields,
  ].map(({id}) => id);
  const selectorsById = useSelectorsById({includeArchivedSelectors: true});
  const taskIdToNewStatusId: {[id: string]: string} = {};
  props.tasksWithTerminalStatus.forEach((task) => {
    taskIdToNewStatusId[task.id] = task.statusId;
  });

  return (
    <BenchlingQuery<TaskUpdateConfirmationModalQuery, TaskUpdateConfirmationModalQueryVariables>
      query={TASK_UPDATE_CONFIRMATION_MODAL_QUERY}
      variables={{processTaskIds}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data) => {
        if (selectorsById === AsyncStatus.LOADING) {
          return <CenteredHorizontalThrobber />;
        }
        if (isInaccessibleInfo(data.processTasks) || !selectorsAreLoaded(selectorsById)) {
          return null;
        }
        const fetchedTasks = data.processTasks as Array<ProcessTask>;
        const fetchedTasksById = fetchedTasks.reduce(
          (acc, curr) => {
            acc[curr.id] = curr;
            return acc;
          },
          {} as {[id: string]: ProcessTask}
        );

        return (
          <>
            {props.tasksWithTerminalStatus.length > 0 && (
              <>
                <div>
                  Tasks will be updated to a terminal status upon confirmation. You won’t be able to further
                  modify task statuses or create additional outputs for these tasks after submitting this
                  update.
                  <br />
                  <br />
                  Any outputs or tasks that are part of a flowchart will be mapped to the next step when the
                  task status is set to {DefaultStatusDisplayName.COMPLETED}.
                </div>
                <ConfirmationModalContent
                  updatedTasks={props.tasksWithTerminalStatus}
                  fetchedTasksById={fetchedTasksById}
                  taskSchema={props.taskSchema}
                  taskIdToNewStatusId={taskIdToNewStatusId}
                  selectorsById={selectorsById}
                />
              </>
            )}
            {props.tasksAlreadyMappedWithUpdatedFields.length > 0 && (
              <>
                <div className={props.tasksWithTerminalStatus.length > 0 ? css(styles.extraMargin) : ''}>
                  The following changes are being made to tasks that have already been mapped. These changes
                  will not pass on to the corresponding descendant tasks or outputs in the next step.
                </div>
                <ConfirmationModalContent
                  updatedTasks={props.tasksAlreadyMappedWithUpdatedFields}
                  fetchedTasksById={fetchedTasksById}
                  taskSchema={props.taskSchema}
                  selectorsById={selectorsById}
                />
              </>
            )}
          </>
        );
      }}
    </BenchlingQuery>
  );
}

function TaskUpdateConfirmationModalWithMessage({
  tasksWithTerminalStatus,
  numTasksAlreadyMappedWithUpdatedFields,
  taskSchema,
}: {
  tasksWithTerminalStatus: Array<EditExistingTask & {statusId: string}>;
  numTasksAlreadyMappedWithUpdatedFields: number;
  taskSchema: CreateTaskTableSchemaWithStatusLifecycleParams;
}): JSX.Element {
  const numTasksBeingUpdated = tasksWithTerminalStatus.length + numTasksAlreadyMappedWithUpdatedFields;
  const numTasksWithTerminalStatus = tasksWithTerminalStatus.length;

  const getNumTasksOfStatusType = (statusType: ProcessTaskStatusType): number =>
    tasksWithTerminalStatus.filter(
      (task) => getStatusFromId(task.statusId, taskSchema.statusLifecycle)?.statusType === statusType
    ).length;

  const numCompletedTasks = getNumTasksOfStatusType(ProcessTaskStatusType.COMPLETED);
  const numCancelledTasks = getNumTasksOfStatusType(ProcessTaskStatusType.CANCELLED);
  const numFailedTasks = getNumTasksOfStatusType(ProcessTaskStatusType.FAILED);

  const getCancelledOrFailedMessage = (): JSX.Element | null => {
    const cancelledMessage = (
      <div className={css(styles.statusUpdateMessageWrapper)}>
        <span>
          {numCancelledTasks} {pluralize(numCancelledTasks, 'task')} will be marked as
        </span>
        &nbsp;
        <InlineProcessTaskStatusDisplay
          statusType={ProcessTaskStatusType.CANCELLED}
          displayName={DefaultStatusDisplayName.CANCELLED}
        />
      </div>
    );
    const failedMessage = (
      <div className={css(styles.statusUpdateMessageWrapper)}>
        <span>
          {numFailedTasks} {pluralize(numFailedTasks, 'task')} will be marked as
        </span>
        &nbsp;
        <InlineProcessTaskStatusDisplay
          statusType={ProcessTaskStatusType.FAILED}
          displayName={DefaultStatusDisplayName.FAILED}
        />
      </div>
    );
    if (numCancelledTasks > 0 && numFailedTasks > 0) {
      return (
        <div className={css(styles.statusUpdateMessageWrapper)}>
          {cancelledMessage} <span>&nbsp;and&nbsp;</span> {failedMessage}
        </div>
      );
    } else if (numCancelledTasks > 0) {
      return cancelledMessage;
    } else if (numFailedTasks > 0) {
      return failedMessage;
    }
    return null;
  };

  if (numTasksWithTerminalStatus === 0) {
    return (
      <div>
        You are about to submit updates for {numTasksAlreadyMappedWithUpdatedFields}{' '}
        {pluralize(numTasksAlreadyMappedWithUpdatedFields, 'task')} that{' '}
        {pluralize(numTasksAlreadyMappedWithUpdatedFields, 'has', 'have')} already been mapped. These new
        updates <strong>won't</strong> be mapped to the next tasks or outputs in the flowchart.
      </div>
    );
  }
  return (
    <div>
      You're about to update {numTasksBeingUpdated} {pluralize(numTasksBeingUpdated, 'task')}. You won't be
      able to change statuses or add outputs to these tasks.
      <br />
      <br />
      <>
        {numCompletedTasks > 0 && (
          <>
            <div className={css(styles.statusUpdateMessageWrapper)}>
              <span>
                {numCompletedTasks} {pluralize(numCompletedTasks, 'task')} will be marked as
              </span>
              &nbsp;
              <InlineProcessTaskStatusDisplay
                statusType={ProcessTaskStatusType.COMPLETED}
                displayName={DefaultStatusDisplayName.COMPLETED}
              />
            </div>
            <ul>
              <li>Tasks and outputs in a flowchart will be mapped to the next step</li>
            </ul>
          </>
        )}
        {(numCancelledTasks > 0 || numFailedTasks > 0) && (
          <>
            {getCancelledOrFailedMessage()}
            <ul>
              <li>Tasks and outputs in a flowchart won't be mapped to the next step</li>
            </ul>
          </>
        )}
      </>
      {numTasksAlreadyMappedWithUpdatedFields > 0 && (
        <>
          {numTasksAlreadyMappedWithUpdatedFields} {pluralize(numTasksAlreadyMappedWithUpdatedFields, 'task')}{' '}
          will be updated that {pluralize(numTasksAlreadyMappedWithUpdatedFields, 'has', 'have')} already been
          mapped.
          <ul>
            <li>
              These new updates <strong>won't</strong> be mapped to the next tasks or outputs in the
              flowchart.
            </li>
          </ul>
        </>
      )}
    </div>
  );
}

export default function TaskUpdateConfirmationModal(props: TaskUpdateConfirmationModalProps): JSX.Element {
  const numTasks = props.tasksWithTerminalStatus.length + props.tasksAlreadyMappedWithUpdatedFields.length;
  const useModalWithTable = numTasks <= BenchlingConfig.WORKFLOWS_MAX_ROWS_FOR_LEGACY_CONFIRMATION_MODALS;
  return (
    <Modal
      title="Confirm task update"
      expandHeight={useModalWithTable}
      large={useModalWithTable}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={props.onRequestSubmit}
      buttons={[
        {
          cancel: true,
          text: 'Cancel',
        },
        {
          submit: true,
          text: 'Confirm',
          style: ButtonStyle.PRIMARY,
        },
      ]}
    >
      {useModalWithTable ? (
        <TaskUpdateConfirmationModalWithTable {...props} />
      ) : (
        <TaskUpdateConfirmationModalWithMessage
          tasksWithTerminalStatus={props.tasksWithTerminalStatus}
          numTasksAlreadyMappedWithUpdatedFields={props.tasksAlreadyMappedWithUpdatedFields.length}
          taskSchema={props.taskSchema}
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  taskTable: {
    marginTop: '8px',
    marginRight: '16px',
  },
  extraMargin: {
    marginTop: '16px',
  },
  statusUpdateMessageWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
});
